import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ExpandedState, Row, Updater } from '@tanstack/react-table';
import logger from '@ayocore/platform/common/helpers/logger';
import type { RepositoryTableSubRows } from './types';
import {
  collectSelectableRows,
  findChildKeyCollisions,
  resolveSubRows,
  survivingExpandedParents,
  treeRowKey,
  type SubRowsState,
} from './subRowsTree';

/**
 * Состояние дерева раскрываемых строк (`subRows`) обычного режима таблицы.
 *
 * Держит раскрытие и подгруженных детей и отдаёт `RTable` всё, что нужно для
 * дерева: данные, ключи строк, извлекатель детей, предикат раскрытия.
 * Без `subRows` хук прозрачен — таблица работает ровно как раньше, поэтому
 * остальные списки системы эта правка не задевает.
 *
 * Жизненный цикл детей привязан к ЗАГРУЗКЕ СТРАНИЦЫ (`pageVersion`), а не к
 * ссылке на строки: селектор данных при равных строках отдаёт прежнюю ссылку,
 * и «обновил список после правки ребёнка» было бы неотличимо от «ничего не
 * произошло». На каждую загрузку прежние дети устаревают. Раскрытие родителей,
 * оставшихся на странице, сохраняем и грузим их детей заново: после «добавил
 * фото» или «вывел из группы» раскрытая группа не должна схлопываться у
 * пользователя под рукой.
 *
 * Та же загрузка пересобирает ключи строк: `canExpand` может зависеть от режима
 * списка (фильтр «все заходы»), и строка, переставшая быть родителем, обязана
 * потерять ключ `tree:…` — иначе он уехал бы в выбор вместо ключа сущности.
 */
export function useSubRowsTree<T extends Record<string, unknown>>(params: {
  rows: T[];
  subRows?: RepositoryTableSubRows<T>;
  resolveKey: (row: T, index?: number) => string;
  /** Номер успешной загрузки страницы (RepositoryControllerState.loadVersion). */
  pageVersion: number;
  /**
   * Показать дерево плоским. Режим «только выделенные» — список конкретных
   * выбранных сущностей: раскрытие группы вернуло бы в него и дубли выбранных
   * детей, и невыбранных участников.
   */
  flat: boolean;
}) {
  const { rows, subRows, resolveKey, pageVersion, flat } = params;
  const enabled = subRows !== undefined && !flat;

  // Функции потребителя читаем через ref: объект `subRows` обычно передают
  // литералом, и его identity меняется на каждый рендер родителя — пересобирать
  // дерево из-за этого незачем. Дерево пересобирается по смене данных.
  const subRowsRef = useRef(subRows);
  subRowsRef.current = subRows;
  const canExpand = useCallback((row: T) => subRowsRef.current?.canExpand(row) ?? false, []);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [childrenByParent, setChildrenByParent] = useState<Record<string, SubRowsState<T>>>({});
  const expandedRef = useRef(expanded);
  expandedRef.current = expanded;
  const childrenRef = useRef(childrenByParent);
  childrenRef.current = childrenByParent;

  /**
   * «Эпоха» страницы. Ответ загрузки, начатой на прошлой странице, не должен
   * лечь на новую: иначе быстрый переход между страницами показал бы под
   * родителем чужих детей.
   */
  const epochRef = useRef(0);

  const loadChildren = useCallback((parentKey: string, row: T) => {
    const source = subRowsRef.current;
    if (!source) return;
    const epoch = epochRef.current;
    setChildrenByParent(prev => ({
      ...prev,
      [parentKey]: { status: 'loading', rows: prev[parentKey]?.rows ?? [] },
    }));
    source.load(row).then(
      loaded => {
        if (epochRef.current !== epoch) return;
        setChildrenByParent(prev => ({ ...prev, [parentKey]: { status: 'ready', rows: loaded } }));
      },
      (error: unknown) => {
        if (epochRef.current !== epoch) return;
        logger.error(error, '[RepositoryTable] subRows.load');
        setChildrenByParent(prev => ({
          ...prev,
          [parentKey]: {
            status: 'error',
            rows: [],
            error: error instanceof Error ? error.message : 'Не удалось загрузить строки',
          },
        }));
      }
    );
  }, []);

  // Строки читаем через ref: эффект ниже привязан к ФАКТУ загрузки, а не к
  // смене ссылки на строки (она при равных данных не меняется).
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // Каждая загрузка страницы: новая эпоха, дети сброшены, живые раскрытия — заново.
  useEffect(() => {
    if (!enabled) return;
    epochRef.current += 1;
    const surviving = survivingExpandedParents({
      expanded: expandedRef.current,
      rows: rowsRef.current,
      canExpand,
      resolveKey,
    });
    setChildrenByParent({});
    setExpanded(Object.fromEntries(surviving.map(({ key }) => [key, true])));
    for (const { key, row } of surviving) loadChildren(key, row);
  }, [enabled, pageVersion, canExpand, resolveKey, loadChildren]);

  // Коллизия ключей — один чекбокс на две строки. Таблица её не разрулит,
  // поэтому громко сообщаем разработчику (см. RepositoryTableSubRows.load).
  useEffect(() => {
    if (!enabled) return;
    const collisions = findChildKeyCollisions({ rows, childrenByParent, canExpand, resolveKey });
    if (collisions.length > 0) {
      logger.warn('[RepositoryTable] subRows: ключи детей совпали с корневыми строками', {
        collisions,
      });
    }
  }, [enabled, rows, childrenByParent, canExpand, resolveKey]);

  /**
   * Раскрытие меняет только TanStack (шеврон, клик по строке) — сюда. Отсюда же
   * стартует догрузка: одна точка входа, как бы строку ни раскрыли.
   */
  const onExpandedChange = useCallback(
    (updater: Updater<ExpandedState>) => {
      const current = expandedRef.current;
      const next = typeof updater === 'function' ? updater(current) : updater;
      // `true` — «раскрыть всё» TanStack. Для дерева с ленивыми детьми это
      // запрос на всю страницу разом; такого действия в таблице нет.
      if (next === true) return;
      setExpanded(next);
      rows.forEach((row, index) => {
        if (!canExpand(row)) return;
        const key = treeRowKey({ row, index, isChild: false, canExpand, resolveKey });
        const opened = next[key] && !current[key];
        const state = childrenRef.current[key];
        if (opened && (!state || state.status === 'error')) loadChildren(key, row);
      });
    },
    [rows, canExpand, resolveKey, loadChildren]
  );

  /**
   * Данные для таблицы. Новый массив на каждую загрузку детей — не прихоть:
   * TanStack мемоизирует модель строк по `data`, и без смены массива свежие
   * дети не попали бы в таблицу. Сами объекты строк прежние.
   */
  const data = useMemo(
    () => (enabled ? rows.slice() : rows),
    // childrenByParent и pageVersion в зависимостях намеренно: новый массив на
    // каждую загрузку детей и страницы заставляет TanStack пересобрать модель —
    // а с ней и ключи строк (canExpand мог смениться вместе с режимом списка).
    [enabled, rows, childrenByParent, pageVersion]
  );

  // Корневые строки страницы — по ним getSubRows отличает корень от ребёнка
  // (см. resolveSubRows: иначе двойник родителя уводит дерево в рекурсию).
  const rootRows = useMemo(() => new Set(rows), [rows]);

  const getSubRows = useCallback(
    (row: T, index: number) =>
      resolveSubRows({ row, index, rootRows, childrenByParent, canExpand, resolveKey }),
    [rootRows, childrenByParent, canExpand, resolveKey]
  );

  const getRowId = useCallback(
    (row: T, index: number, parent?: Row<T>) =>
      treeRowKey({ row, index, isChild: parent !== undefined, canExpand, resolveKey }),
    [canExpand, resolveKey]
  );

  /** Раскрываемая корневая строка — узел-группа: раскрывается, не выбирается. */
  const isParentRow = useCallback(
    (row: Row<T>) => row.depth === 0 && canExpand(row.original),
    [canExpand]
  );

  const childrenState = useCallback(
    (rowId: string): SubRowsState<T> | undefined => childrenRef.current[rowId],
    []
  );

  /** Выбранные строки для массовых действий: корневые и подгруженные дети. */
  const resolveSelectedRows = useCallback(
    (rowSelection: Record<string, boolean>): T[] =>
      collectSelectableRows({ rows, childrenByParent, canExpand, resolveKey })
        .filter(({ key }) => rowSelection[key])
        .map(({ row }) => row),
    [rows, childrenByParent, canExpand, resolveKey]
  );

  const hasAnyParent = useMemo(
    () => enabled && rows.some(row => canExpand(row)),
    // pageVersion — по той же причине, что у data: canExpand мог смениться
    // вместе с режимом списка при тех же строках.
    [enabled, rows, canExpand, pageVersion]
  );

  return {
    enabled,
    data,
    expanded,
    onExpandedChange,
    getSubRows,
    getRowId,
    isParentRow,
    childrenState,
    resolveSelectedRows,
    hasAnyParent,
    column: subRows?.column,
  };
}
